// <Drawer> — slide-over drawer; integrates with DrawerHost singleton.
//
// Closes DESIGN_POLICY.md §5 "Drawers do not stack" and a11y-plan.md §M.1
// (drawer focus trap, ESC closes, returns focus to opener).
//
// The Drawer itself does not enforce non-stacking; the host (drawerHost.ts)
// auto-closes any prior drawer when a new one opens. The Drawer participates
// by registering on mount via useDrawerHost().

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '../tokens';
import { useReducedMotion } from '../hooks';
import { useDrawerHost } from '@/state/drawerHost';

export type DrawerSide = 'left' | 'right';

export interface DrawerProps {
  /** Stable id; used by the host to prevent stacking. */
  id: string;
  side?: DrawerSide;
  title: string;
  children: ReactNode;
  /** Width in px or any CSS length; default tokens.layout.rightPanel. */
  width?: number | string;
  /** When true, the drawer is non-modal (no scrim, no focus trap, role="complementary"). */
  nonModal?: boolean;
  /** Required closer; called on ESC + close button + auto-close from host. */
  onClose(): void;
  /** Optional aria-labelledby override; default the rendered title. */
  labelledBy?: string;
  className?: string;
  'data-testid'?: string;
}

export function Drawer({
  id,
  side = 'right',
  title,
  children,
  width,
  nonModal = false,
  onClose,
  labelledBy,
  className,
  'data-testid': testId,
}: DrawerProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const reduced = useReducedMotion();
  const openDrawer = useDrawerHost((s) => s.open);
  const closeDrawer = useDrawerHost((s) => s.close);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const openerRef = useRef<HTMLElement | null>(null);

  // Register with the singleton host on mount; the host closes any previous
  // drawer that was open. Unregister on unmount.
  //
  // Important: do not depend on the whole host store object or an inline
  // `onClose` identity — both change when `open()` updates `openId`, which
  // would re-run this effect, call `close()` in cleanup, and loop forever.
  useEffect(() => {
    openDrawer(id, () => {
      onCloseRef.current();
    });
    return () => {
      closeDrawer(id);
    };
  }, [id, openDrawer, closeDrawer]);

  useEffect(() => {
    if (nonModal) return;
    openerRef.current = (document.activeElement as HTMLElement) ?? null;
    const node = dialogRef.current;
    if (!node) return;
    const focusable = node.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      if (!node) return;
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openerRef.current?.focus?.();
    };
  }, [nonModal, onClose]);

  const portalReady = typeof document !== 'undefined';
  if (!portalReady) return null;

  const resolvedWidth = width ?? tokens.layout.rightPanel;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: tokens.zIndex.dock,
        pointerEvents: nonModal ? 'none' : 'auto',
      }}
    >
      {!nonModal ? (
        <div
          aria-hidden="true"
          onClick={onClose}
          style={{
            position: 'absolute',
            inset: 0,
            background: tokens.color.overlay.scrim,
            pointerEvents: 'auto',
          }}
        />
      ) : null}
      <aside
        ref={dialogRef}
        role={nonModal ? 'complementary' : 'dialog'}
        aria-modal={nonModal ? undefined : 'true'}
        aria-labelledby={labelledBy ?? titleId}
        data-testid={testId}
        className={className}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          [side]: 0,
          width: resolvedWidth,
          maxWidth: '100%',
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          boxShadow: tokens.elevation[2],
          padding: tokens.space[4],
          overflowY: 'auto',
          pointerEvents: 'auto',
          transitionProperty: 'transform',
          transitionDuration: `${reduced ? 0 : tokens.motion.duration.slow}ms`,
          transitionTimingFunction: tokens.motion.easing.decel,
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: tokens.space[2],
            marginBottom: tokens.space[3],
          }}
        >
          <h2
            id={titleId}
            style={{
              margin: 0,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.semibold,
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              color: tokens.color.ink.secondary,
              width: tokens.density.touchMin,
              height: tokens.density.touchMin,
              borderRadius: tokens.radius.sm,
              fontSize: tokens.font.size.lg,
            }}
          >
            ×
          </button>
        </header>
        <div>{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
